// Sign-in screen. Shown whenever there is no valid session.
//
// Posts to /anixify/login, which sets an HttpOnly session cookie. This page
// never sees or stores a credential after the request: no token in
// localStorage, nothing in a global. The cookie is the whole session.
//
// ⚠️ THE VISUAL LAYER IS THE ONLY THING THE REDESIGN TOUCHED. `signIn`, the
// submit handler, the disabled rule, the five testids (login-screen,
// login-username, login-password, login-submit, login-error), both
// autoComplete hints and autoFocus are byte-for-byte what they were. A
// password manager keys on those autoComplete values, so they are contract,
// not decoration.

import { useState } from "react";
import { BRAND } from "@/lib/brand";
import { SESSION_EXPIRED_TEXT, useAuth } from "@/auth/AuthContext";
import {
  colors, semanticColors, heights, spacing, radius, typography, layout, shadows,
} from "@/styles/tokens";

export default function LoginPage() {
  const { signIn, sessionExpired } = useAuth();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Which control owns focus. Inline styles cannot express :focus-visible, and
  // the alternative — a rule in index.css — would restyle every .btn-primary
  // in the app from a login-screen change. Local state keeps the blast radius
  // at this file.
  const [focused, setFocused] = useState<"login" | "password" | "submit" | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError("");
    setBusy(true);
    const result = await signIn(login.trim(), password);
    setBusy(false);
    // On success the provider flips status to "in" and this page unmounts,
    // so there is nothing to reset here.
    if (!result.ok) setError(result.error || "Sign-in failed.");
  }

  const disabled = busy || !login.trim() || !password;

  // ⚠️ FOCUS IS SIGNALLED TWICE, and that is deliberate — the same rule
  // shadows.focusRing carries in tokens.ts. The halo alone is a colour-only
  // cue; the border changes to the accent as well so the state survives a
  // colour-blind or low-contrast viewing.
  const field = (name: "login" | "password"): React.CSSProperties => ({
    height: heights.inputLg,
    width: "100%",
    padding: `0 ${spacing[14]}px`,
    border: `1px solid ${focused === name ? semanticColors.accent : semanticColors.border}`,
    boxShadow: focused === name ? shadows.focusRing : "none",
    borderRadius: radius.lg,
    fontSize: typography.fontSize.base,
    color: semanticColors.textPrimary,
    outline: "none",
    backgroundColor: focused === name ? colors.white : semanticColors.panelBg,
    transition: "border-color 120ms, box-shadow 120ms, background-color 120ms",
    // Without this the input inherits the UA's own font stack and renders in a
    // different face from every label around it.
    fontFamily: "inherit",
    boxSizing: "border-box",
  });

  const label: React.CSSProperties = {
    display: "block",
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: semanticColors.textSecondary,
    marginBottom: spacing[6],
  };

  return (
    <div
      data-testid="login-screen"
      style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.white,
        padding: spacing[24],
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: layout.loginCardWidth,
          // ⚠️ ALREADY CAPPED, AND DELIBERATELY LEFT ALONE. This is 100% of the
          // wrapper's CONTENT box, and the wrapper carries padding: spacing[24]
          // -- so the effective cap is 100vw - 48px, which is more generous
          // than overlayMaxWidth's 16px gutter. Adding the shared constant here
          // would TIGHTEN the sign-in card for no reason. The six overlays that
          // needed the cap are listed on overlayMaxWidth itself.
          maxWidth: "100%",
          backgroundColor: colors.white,
          borderRadius: radius.xl,
          // No border. The existing elevation separates the white card from
          // the plain white page without adding heavier chrome.
          boxShadow: shadows.card,
          // Generous on a desktop, and it gives way on a narrow phone instead
          // of squeezing the fields — clamp is the only way to say that from
          // an inline style, and both ends come from the spacing scale.
          padding: `clamp(${spacing[24]}px, 7vw, ${spacing[40]}px)`,
          boxSizing: "border-box",
        }}
      >
        {/* Header block: centered as a unit. The fields below stay
            left-aligned — a centered label above a left-aligned input reads
            as a mistake, and forms are scanned down their left edge. */}
        <div style={{ textAlign: "center", marginBottom: spacing[32] }}>
          {/* THE NAME, AS TEXT (owner decision D81, 2026-09-29). The asset is
              a square symbol with no wordmark, so the brand name is set in
              the app's own font and tokens (the build's brand pack name, P1-12). A logo image can
              replace it later. */}
          <img
            src={BRAND.logo}
            alt={BRAND.markAlt}
            style={{
              width: heights.loginLogo,
              height: "auto",
              display: "block",
              margin: `0 auto ${spacing[12]}px`,
            }}
          />
          <div
            data-testid="login-wordmark"
            style={{
              fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold,
              letterSpacing: typography.letterSpacing.tight, color: semanticColors.textPrimary,
              marginBottom: spacing[20],
            }}
          >
            {BRAND.name}
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: typography.fontSize.xxxl,
              fontWeight: typography.fontWeight.semibold,
              letterSpacing: typography.letterSpacing.tight,
              color: semanticColors.textPrimary,
              lineHeight: typography.lineHeight.tight,
            }}
          >
            Sign in
          </h1>
        </div>

        <div style={{ marginBottom: spacing[16] }}>
          <label htmlFor="login-user" style={label}>Login</label>
          <input
            id="login-user"
            data-testid="login-username"
            type="text"
            autoComplete="username"
            autoFocus
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            onFocus={() => setFocused("login")}
            onBlur={() => setFocused(null)}
            style={field("login")}
          />
        </div>

        <div style={{ marginBottom: spacing[24] }}>
          <label htmlFor="login-password" style={label}>Password</label>
          <input
            id="login-password"
            data-testid="login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setFocused("password")}
            onBlur={() => setFocused(null)}
            style={field("password")}
          />
        </div>

        {/* session-expired-redirect: why the sign-in screen came up. */}
        {sessionExpired && !error && (
          <div
            data-testid="login-session-expired"
            role="status"
            style={{
              marginBottom: spacing[16],
              padding: `${spacing[10]}px ${spacing[12]}px`,
              backgroundColor: semanticColors.warningBg,
              border: `1px solid ${semanticColors.warningBorder}`,
              borderRadius: radius.lg,
              color: colors.amber800,
              fontSize: typography.fontSize.sm,
              lineHeight: typography.lineHeight.base,
            }}
          >
            {SESSION_EXPIRED_TEXT}
          </div>
        )}

        {error && (
          <div
            data-testid="login-error"
            role="alert"
            style={{
              marginBottom: spacing[16],
              padding: `${spacing[10]}px ${spacing[12]}px`,
              backgroundColor: semanticColors.destructiveBg,
              border: `1px solid ${semanticColors.destructiveBorder}`,
              borderRadius: radius.lg,
              color: semanticColors.destructive,
              fontSize: typography.fontSize.sm,
              lineHeight: typography.lineHeight.base,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          data-testid="login-submit"
          disabled={disabled}
          // ⚠️ THE CLASS IS DROPPED WHEN DISABLED, on purpose. `.btn-primary:hover`
          // sets the accent with `!important`, and CSS `:hover` DOES match a
          // disabled button — so keeping the class would make a greyed-out,
          // unclickable button light up bright green under the cursor. Enabled,
          // it still supplies the app-wide hover and :active press. Dropping it
          // costs nothing else: the `color` it forces (#1A1A1A) is the same
          // value `semanticColors.onAccent` sets inline below.
          className={disabled ? undefined : "btn-primary"}
          onFocus={() => setFocused("submit")}
          onBlur={() => setFocused(null)}
          style={{
            width: "100%",
            height: heights.inputLg,
            backgroundColor: disabled ? semanticColors.textDisabled : semanticColors.accent,
            // ⚠️ NEVER white on this accent — it is a lime green and white on
            // it is 1.6:1. onAccent is the near-black the token file mandates.
            color: semanticColors.onAccent,
            border: "none",
            borderRadius: radius.lg,
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.medium,
            fontFamily: "inherit",
            cursor: disabled ? "default" : "pointer",
            boxShadow: focused === "submit" && !disabled ? shadows.focusRing : "none",
          }}
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
